import { getCarNumberMatchKey } from "./dispatchConflictUtils";

const trimText = (value) => String(value || "").trim();

const sameText = (a, b) => trimText(a) !== "" && trimText(a) === trimText(b);

const profileCarNumberFromCar = (car) => {
  const only = trimText(car?.car_number_only);
  if (only) return only;
  return getCarNumberMatchKey(car?.car_number || "");
};

export async function findCarForDriverSync(supabase, company, carNumber) {
  const target = trimText(carNumber);
  if (!company || !target) return null;

  const targetKey = getCarNumberMatchKey(target);
  const { data, error } = await supabase
    .from("cars")
    .select("id, car_number, car_number_only, driver, driver_email, phone")
    .eq("company", company);

  if (error) throw error;

  const matches = (data || []).filter((car) => (
    trimText(car.car_number) === target ||
    trimText(car.car_number_only) === target ||
    getCarNumberMatchKey(car.car_number || car.car_number_only || "") === targetKey
  ));

  if (matches.length > 1) {
    throw new Error(`${target} 차량번호와 일치하는 차량이 여러 대입니다. 차량정보에서 정확한 차량번호를 확인해 주세요.`);
  }

  return matches[0] || null;
}

async function clearProfileCarNumberIfLinked(supabase, company, car, driver) {
  if (!company || !car) return;

  let query = supabase
    .from("profiles")
    .select("id, email, phone, driver_name, car_number")
    .eq("role", "driver")
    .eq("company", company);

  if (driver?.email) query = query.eq("email", driver.email);
  else if (driver?.phone) query = query.eq("phone", driver.phone);
  else if (driver?.driver_name) query = query.eq("driver_name", driver.driver_name);
  else return;

  const { data, error } = await query.limit(2);
  if (error) throw error;

  const rows = data || [];
  if (rows.length !== 1) return;

  const carKey = getCarNumberMatchKey(car.car_number || car.car_number_only || "");
  const profileKey = getCarNumberMatchKey(rows[0].car_number || "");
  if (profileKey && carKey && profileKey !== carKey) return;

  const { error: clearError } = await supabase
    .from("profiles")
    .update({ car_number: null })
    .eq("id", rows[0].id)
    .eq("company", company);
  if (clearError) throw clearError;
}

export async function syncCarFromDriverProfile(supabase, {
  company,
  previousCarNumber,
  previousEmail,
  previousDriverName,
  nextCarNumber,
  driverName,
  phone,
  email,
}) {
  const prevCar = trimText(previousCarNumber);
  const nextCar = trimText(nextCarNumber);

  if (prevCar && getCarNumberMatchKey(prevCar) !== getCarNumberMatchKey(nextCar)) {
    const oldCar = await findCarForDriverSync(supabase, company, prevCar);
    if (oldCar) {
      const linkedByEmail = sameText(oldCar.driver_email, previousEmail);
      const linkedByName = sameText(oldCar.driver, previousDriverName);
      if (linkedByEmail || linkedByName) {
        const { error } = await supabase
          .from("cars")
          .update({ driver: null, driver_email: null, phone: null })
          .eq("id", oldCar.id)
          .eq("company", company);
        if (error) throw error;
      }
    }
  }

  if (!nextCar) return;

  const car = await findCarForDriverSync(supabase, company, nextCar);
  if (!car) return;

  const currentDriverIsDifferent =
    (car.driver_email && email && car.driver_email !== email) ||
    (!car.driver_email && car.driver && driverName && car.driver !== driverName);

  if (currentDriverIsDifferent) {
    await clearProfileCarNumberIfLinked(supabase, company, car, {
      email: car.driver_email,
      phone: car.phone,
      driver_name: car.driver,
    });
  }

  const { error } = await supabase
    .from("cars")
    .update({
      driver: driverName || null,
      driver_email: email || null,
      phone: phone || null,
    })
    .eq("id", car.id)
    .eq("company", company);
  if (error) throw error;
}

export async function syncDriverProfileFromCar(supabase, {
  company,
  car,
  previousCar,
}) {
  if (!company || !car?.id) return null;

  const carProfileNumber = profileCarNumberFromCar(car);
  const previousDriver = {
    email: previousCar?.driver_email,
    phone: previousCar?.phone,
    driver_name: previousCar?.driver,
  };

  const driverChanged =
    trimText(previousCar?.driver_email) !== trimText(car.driver_email) ||
    trimText(previousCar?.phone) !== trimText(car.phone) ||
    trimText(previousCar?.driver) !== trimText(car.driver);

  if (driverChanged) {
    await clearProfileCarNumberIfLinked(supabase, company, previousCar || car, previousDriver);
  }

  const driverName = trimText(car.driver);
  const driverEmail = trimText(car.driver_email);
  const driverPhone = trimText(car.phone);
  if (!driverName && !driverEmail && !driverPhone) return null;

  let query = supabase
    .from("profiles")
    .select("id, email, phone, driver_name, car_number")
    .eq("role", "driver")
    .eq("company", company);

  if (driverEmail) query = query.eq("email", driverEmail);
  else if (driverPhone) query = query.eq("phone", driverPhone);
  else query = query.eq("driver_name", driverName);

  const { data, error } = await query.limit(2);
  if (error) throw error;

  const matches = data || [];
  if (matches.length === 0) {
    if (driverName) {
      throw new Error("기사명과 일치하는 기사 정보를 찾을 수 없습니다.");
    }
    return null;
  }
  if (matches.length > 1) {
    throw new Error(`${driverName || driverPhone} 기사 정보가 여러 명입니다. 기사 전화번호나 이메일로 정확히 선택해 주세요.`);
  }

  const currentProfile = matches[0];
  const profileCarKey = getCarNumberMatchKey(currentProfile.car_number || "");
  const nextCarKey = getCarNumberMatchKey(carProfileNumber || "");
  if (profileCarKey && nextCarKey && profileCarKey !== nextCarKey) {
    const oldCar = await findCarForDriverSync(supabase, company, currentProfile.car_number);
    if (oldCar && oldCar.id !== car.id) {
      const linkedByEmail = sameText(oldCar.driver_email, currentProfile.email);
      const linkedByPhone = sameText(oldCar.phone, currentProfile.phone);
      const linkedByName = sameText(oldCar.driver, currentProfile.driver_name);
      if (linkedByEmail || linkedByPhone || linkedByName) {
        const { error: clearOldCarError } = await supabase
          .from("cars")
          .update({ driver: null, driver_email: null, phone: null })
          .eq("id", oldCar.id)
          .eq("company", company);
        if (clearOldCarError) throw clearOldCarError;
      }
    }
  }

  const payload = {
    car_number: carProfileNumber || null,
  };
  if (driverPhone) payload.phone = driverPhone;
  if (driverName) payload.driver_name = driverName;
  if (driverEmail) payload.email = driverEmail;

  const { error: syncError } = await supabase
    .from("profiles")
    .update(payload)
    .eq("id", currentProfile.id)
    .eq("company", company);
  if (syncError) throw syncError;

  return currentProfile;
}
